import { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../client/config';
import { describeApiError } from './toast';

// =============================================================================
// useContentScopeAccess — 현재 사용자의 그룹 스코프 정보(/api/device-groups).
// 콘텐츠 만들기 가능 여부와 Owner 여부(공개 범위 변경은 Owner만)를 화면이 여기서 읽는다.
// 옛 ContentScopeControls(소유·공유 패널)는 2026-09-15 대교 결정으로 화면에서 제거되어 훅만 남았다.
// =============================================================================

export type ScopeOptions = {
  groups: Array<{ id: string; name: string }>;
  actor: {
    central: boolean;
    role: string | null;
    enforced: boolean;
    groups: Array<{ groupId: string; role: string }>;
  };
};

async function scopeRequest<T>(path: string, init: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}/api/${path}`, { credentials: 'include', ...init });
  if (!response.ok) {
    const body = await response.text();
    throw new Error(`${response.status} ${body}`);
  }
  return response.json() as Promise<T>;
}

/**
 * 만든 콘텐츠를 넣을 수 있는 자리 수 — 중앙 관리자의 '내 자료' 1 + 담당(Admin·Editor) 그룹 수.
 *
 * 2026-09-16 대교 지적 1: "A그룹 어드민이 공통 부분에 요소가 없는 경우, 슬라이드 만들기나
 * 미디어 올리기 버튼이 누를 수 있게 되어 있어. 막아야 함." canCreate만으로는 그룹별 권한을
 * '적용'하지 않은 조직에서 항상 true라 이 구멍이 막히지 않는다. 0이면 만들기를 비활성한다
 * (숨기지 않는다 — "조작부는 움직이지 않는다").
 */
export function writableTargetCount(
  actor: { central: boolean; groups: Array<{ groupId: string; role: string }> } | null | undefined,
): number {
  if (!actor) return 0;
  return (actor.central ? 1 : 0) + actor.groups.filter((group) => ['Admin', 'Editor'].includes(group.role)).length;
}

/** 조직 Owner(중앙)인가 — 공개/비공개 변경은 서버도 이 조건으로만 허용한다. */
export function isScopeOwner(options: ScopeOptions | null): boolean {
  // 테스트·비정상 응답에서 actor가 없을 수 있다 — 닫힌 쪽(false)으로.
  return !!options?.actor?.central && options.actor.role === 'Owner';
}

/** Creation needs actor access even when the library is empty. Fail closed while checking. */
export function useContentScopeAccess() {
  const [options, setOptions] = useState<ScopeOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const reload = useCallback(() => {
    setOptions(null);
    setError(null);
    setAttempt((value) => value + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void scopeRequest<ScopeOptions>('device-groups', { signal: controller.signal })
      .then((result) => { if (!controller.signal.aborted) setOptions(result); })
      .catch((reason: unknown) => { if (!controller.signal.aborted) setError(describeApiError(reason)); });
    return () => controller.abort();
  }, [attempt]);

  useEffect(() => {
    window.addEventListener('bricklit:scope-changed', reload);
    return () => window.removeEventListener('bricklit:scope-changed', reload);
  }, [reload]);

  const actor = options?.actor;
  const canCreate = !!actor && (!actor.enforced || ['Owner', 'Admin', 'Editor'].includes(actor.role ?? '')
    && (actor.central && ['Owner', 'Admin'].includes(actor.role ?? '')
      || actor.groups.some((group) => ['Admin', 'Editor'].includes(group.role))));
  const isOwner = isScopeOwner(options);
  const enforced = !!actor?.enforced;
  // 만들기 게이트는 '권한이 있는가'와 '넣을 자리가 있는가'를 둘 다 본다(2026-09-16 대교 지적 1).
  const writeTargets = writableTargetCount(actor);

  const { ownableGroups, canChooseOwnerGroup, canChooseCommonGroup } = ownerGroupChoices(options);

  return { options, error, reload, canCreate, isOwner, enforced, writeTargets,
    ownableGroups, canChooseOwnerGroup, canChooseCommonGroup };
}

/**
 * 콘텐츠를 어느 그룹 것으로 만들지 고를 수 있는 사람과 그 선택지 (2026-09-18 대교).
 *
 * "두 그룹을 맡은 편집자/관리자도 A그룹에 생성, B그룹에 생성을 할 수 있어야 한다."
 * 격리를 넘는 것과는 다르다 — 남의 그룹 자료를 가져다 쓰는 건 여전히 중앙만 한다. 여기서 여는
 * 건 '내가 맡은 그룹 중 어디에 만들까'뿐이고, 한 곳만 맡았으면 고를 것이 없어 칸을 내지 않는다.
 *
 * 공통(중앙 소유)은 중앙만 고른다. 그룹 구성원이 공통에 올리면 자기 그룹 밖으로 내보내는 셈이라
 * 소유 이전 권한(서버는 조직 Owner만 허용)과 어긋난다.
 */
export function ownerGroupChoices(options: ScopeOptions | null): {
  ownableGroups: Array<{ id: string; name: string }>;
  canChooseOwnerGroup: boolean;
  canChooseCommonGroup: boolean;
} {
  const actor = options?.actor;
  const writable = new Set((actor?.groups ?? [])
    .filter((group) => ['Admin', 'Editor'].includes(group.role)).map((group) => group.groupId));
  const ownableGroups = (options?.groups ?? [])
    .filter((group) => !!actor?.central || writable.has(group.id));
  return {
    ownableGroups,
    canChooseOwnerGroup: !!actor?.central || ownableGroups.length >= 2,
    canChooseCommonGroup: !!actor?.central,
  };
}
